import askAIApi from "../apis/askAIApi";
import {
    addMessage,
    setIsAIResponding,
    setCapturedScreenshot,
    resetPromptSelection,
    setCurrentPromptType,
    setUserQuestion,
    setSmartActions,
    setIsGeneratingSmartActions,
    addSnapshot,
    setSelectedActionId,
    clearSelectedActionId,
    setSuggestNewSnapshot,
    hideSnapshotHeaderBubble,
} from "../reducers/askAI-reducer";
import { ASK_AI_MESSAGE_TYPES, ASK_AI_PROMPT_TYPES } from "../constants/AskAIConstants";
import askAIService from "../services/askAIService";
import { LogFirebase } from "../services/firestore";
import uploadFilesService from "../services/uploadFilesService";
import { CALL_BODY_SCREEN, EXTRACT_TEXT_VIEW } from "../constants/Constants";
import { generateResponseWithActions } from "../utils/smartActions";
import { validateAndSanitizeInput, validateUserMessage } from "../utils/validateUserInput";

/**
 * Handle "Quick Fix" prompt - capture screenshot and generate AI analysis + Smart Actions
 * NEW BEHAVIOR: Returns AI analysis text AND action buttons
 * @param {boolean} isFromBanner - Whether called from NewSnapshotBanner (shows captured image on right side)
 */
export const handleQuickAssist =
    (isFromBanner = false) =>
    async (dispatch, getState) => {
        try {
            LogFirebase("AskAI", "handleQuickAssist_started", "");

            // Capture screenshot from video
            const screenshot = askAIService.captureScreenshot();

            if (!screenshot) {
                LogFirebase("AskAI", "handleQuickAssist_no_screenshot", "");
                return;
            }

            // Hide previous Smart Actions bubbles (keep AI Response bubbles visible)
            dispatch(hideSnapshotHeaderBubble());

            // Create unique snapshot ID
            const snapshotId = `snapshot-${Date.now()}`;

            // Store screenshot as a unique snapshot
            dispatch(
                addSnapshot({
                    id: snapshotId,
                    image: screenshot,
                    capturedAt: new Date().toISOString(),
                })
            );

            // Store screenshot for later use when Smart Action is clicked
            dispatch(setCapturedScreenshot(screenshot));

            // Always show captured screenshot image on right side (for both Quick Fix and NewSnapshotBanner)
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.USER,
                    image: screenshot,
                    text: null,
                    timestamp: new Date().toISOString(),
                    snapshotId: snapshotId,
                })
            );

            // Set AI responding state
            dispatch(setIsAIResponding(true));
            dispatch(setIsGeneratingSmartActions(true));

            // Get full conversation history to preserve context
            const { messages } = getState().askAIReducer;
            // Always pass full conversation history (excluding hidden messages)
            // This ensures Gemini retains context from previous interactions
            const conversationHistory = messages.filter((msg) => !msg.hidden);

            // Call Gemini to get BOTH initial analysis text AND smart actions
            // Pass empty string for initial analysis - relies on system instruction
            const result = await generateResponseWithActions(
                screenshot,
                "", // Empty prompt for initial analysis
                conversationHistory
            );

            // Add AI Response as a SEPARATE message bubble (FIRST)
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.AI,
                    text: result.textResponse,
                    timestamp: new Date().toISOString(),
                    snapshotId: snapshotId,
                })
            );

            // Add Smart Actions as a SEPARATE message bubble (SECOND - after AI response)
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.SMART_ACTIONS,
                    actions: result.actions,
                    timestamp: new Date().toISOString(),
                    snapshotId: snapshotId,
                    showHeader: false, // No header - clean extension of AI response
                })
            );

            // Update smart actions in Redux (for backward compatibility with footer display)
            dispatch(setSmartActions(result.actions));

            // Show suggestion banner if Gemini recommends new snapshot
            if (result.suggestNewSnapshot) {
                dispatch(setSuggestNewSnapshot(true));
            }

            // Reset states
            dispatch(setIsAIResponding(false));
            dispatch(setIsGeneratingSmartActions(false));

            LogFirebase("AskAI", "handleQuickAssist_complete", {
                textLength: result.textResponse.length,
                actionCount: result.actions.length,
            });
        } catch (error) {
            console.error("Error in handleQuickAssist:", error);
            LogFirebase("AskAI", "handleQuickAssist_catch", error?.message);

            // On error, show fallback actions
            dispatch(setSmartActions([]));
            dispatch(setIsAIResponding(false));
            dispatch(setIsGeneratingSmartActions(false));
        }
    };

/**
 * Handle screenshot capture for "Snap & Ask" prompt
 * NEW BEHAVIOR: Only captures screenshot, does NOT auto-send to Gemini
 * User must type (optional) and click Send to trigger AI analysis
 */
export const handleCaptureScreenshot = () => async (dispatch, getState) => {
    try {
        LogFirebase("AskAI", "handleCaptureScreenshot_started", "");

        const screenshot = askAIService.captureScreenshot();

        if (screenshot) {
            // Hide previous Smart Actions bubbles (preserve conversation history)
            dispatch(hideSnapshotHeaderBubble());

            // Create unique snapshot ID
            const snapshotId = `snapshot-${Date.now()}`;

            // Store screenshot as a unique snapshot
            dispatch(
                addSnapshot({
                    id: snapshotId,
                    image: screenshot,
                    capturedAt: new Date().toISOString(),
                })
            );

            // Clear old Smart Actions from previous flow
            dispatch(setSmartActions([]));

            // Clear any existing user question
            dispatch(setUserQuestion(""));

            // Store screenshot and set prompt type
            dispatch(setCapturedScreenshot(screenshot));
            dispatch(setCurrentPromptType(ASK_AI_PROMPT_TYPES.SCREENSHOT_ASK));

            // Clear suggestion banner if showing
            dispatch(setSuggestNewSnapshot(false));

            LogFirebase("AskAI", "handleCaptureScreenshot_success", "");

            // DO NOT call handleGenerateSmartActions here
            // Wait for user to click Send button
        } else {
            LogFirebase("AskAI", "handleCaptureScreenshot_failed", "");
        }
    } catch (error) {
        console.error("Error in handleCaptureScreenshot:", error);
        LogFirebase("AskAI", "handleCaptureScreenshot_catch", error?.message);
    }
};

/**
 * Send screenshot with user question to AI (Snap & Ask flow)
 * NEW BEHAVIOR: Works like Quick Fix - returns AI response + Smart Actions
 * Includes input validation for user question
 * @param {string} screenshot - Base64 screenshot
 * @param {string} question - User's optional text (can be empty)
 */
export const handleSendWithScreenshot = (screenshot, question) => async (dispatch, getState) => {
    try {
        LogFirebase("AskAI", "handleSendWithScreenshot_started", "");

        // Validate user question if provided
        let sanitizedQuestion = null;
        if (question && question.trim()) {
            const validation = validateUserMessage(question);
            if (!validation.valid) {
                dispatch(
                    addMessage({
                        type: ASK_AI_MESSAGE_TYPES.AI,
                        text: validation.error,
                        timestamp: new Date().toISOString(),
                        isError: true,
                    })
                );
                LogFirebase("AskAI", "handleSendWithScreenshot_validation_failed", validation.error);
                return;
            }
            sanitizedQuestion = validation.sanitized;
        }

        // Get current snapshot ID and conversation history
        const { currentSnapshotId } = getState().askAIReducer;

        // Add user message with image and optional sanitized text
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.USER,
                image: screenshot,
                text: sanitizedQuestion, // Can be null if user didn't type anything
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
            })
        );

        // Set AI responding state
        dispatch(setIsAIResponding(true));
        dispatch(setIsGeneratingSmartActions(true));

        // Get full conversation history to preserve context
        const { messages } = getState().askAIReducer;
        // Always pass full conversation history (excluding hidden messages)
        // This ensures Gemini retains context from previous interactions
        const conversationHistory = messages.filter((msg) => !msg.hidden);

        // Build prompt:
        // - If user provided text: use their sanitized question
        // - If no text (image only): pass empty string for initial analysis (relies on system instruction)
        const prompt = sanitizedQuestion || "";

        // Call Gemini to get BOTH AI response text AND smart actions
        const result = await generateResponseWithActions(screenshot, prompt, conversationHistory);

        // Add AI Response as a SEPARATE message bubble (FIRST)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.AI,
                text: result.textResponse,
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
            })
        );

        // Add Smart Actions as a SEPARATE message bubble (SECOND - after AI response)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.SMART_ACTIONS,
                actions: result.actions,
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
                showHeader: false, // No header - clean extension of AI response
            })
        );

        // Update smart actions in Redux (for backward compatibility)
        dispatch(setSmartActions(result.actions));

        // Show suggestion banner if Gemini recommends new snapshot
        if (result.suggestNewSnapshot) {
            dispatch(setSuggestNewSnapshot(true));
        }

        // Reset states
        dispatch(setIsAIResponding(false));
        dispatch(setIsGeneratingSmartActions(false));

        // Clear user question and prompt type, but KEEP capturedScreenshot for Smart Actions
        dispatch(setUserQuestion(""));
        dispatch(setCurrentPromptType(null));

        LogFirebase("AskAI", "handleSendWithScreenshot_success", {
            textLength: result.textResponse.length,
            actionCount: result.actions.length,
            hadUserText: !!question,
        });
    } catch (error) {
        console.error("Error in handleSendWithScreenshot:", error);
        LogFirebase("AskAI", "handleSendWithScreenshot_catch", error?.message);

        // On error, show fallback actions
        dispatch(setSmartActions([]));
        dispatch(setIsAIResponding(false));
        dispatch(setIsGeneratingSmartActions(false));

        // Clear user question and prompt type, but KEEP capturedScreenshot
        dispatch(setUserQuestion(""));
        dispatch(setCurrentPromptType(null));
    }
};

/**
 * Send text-only question to AI with full conversation context
 * Includes input validation and rate limiting
 */
export const handleSendTextOnly = (question) => async (dispatch, getState) => {
    try {
        LogFirebase("AskAI", "handleSendTextOnly_started", "");

        // Validate and sanitize input with rate limiting
        const validation = validateAndSanitizeInput(question);
        if (!validation.valid) {
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.AI,
                    text: validation.error,
                    timestamp: new Date().toISOString(),
                    isError: true, // Flag for styling
                })
            );
            LogFirebase("AskAI", "handleSendTextOnly_validation_failed", validation.error);
            return;
        }

        const sanitizedQuestion = validation.sanitized;

        // Get current conversation history
        const { messages } = getState().askAIReducer;

        // Add user message with sanitized text
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.USER,
                text: sanitizedQuestion,
                timestamp: new Date().toISOString(),
            })
        );

        // Set AI responding state
        dispatch(setIsAIResponding(true));

        // Send to Gemini with full conversation context
        const response = await askAIApi.sendWithContext(messages, {
            text: sanitizedQuestion,
        });

        // Add AI response
        if (response.success) {
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.AI,
                    text: response.data,
                    timestamp: new Date().toISOString(),
                })
            );
            LogFirebase("AskAI", "handleSendTextOnly_success", "");

            // Note: No smart actions generated for text-only input
            // Smart actions only shown after smart action button clicks with images
        } else {
            dispatch(
                addMessage({
                    type: ASK_AI_MESSAGE_TYPES.AI,
                    text: `Error: ${response.error}`,
                    timestamp: new Date().toISOString(),
                })
            );
            LogFirebase("AskAI", "handleSendTextOnly_error", response.error);
        }

        // Reset AI responding state, prompt selection, and clear user question
        dispatch(setIsAIResponding(false));
        dispatch(resetPromptSelection());
        dispatch(setUserQuestion(""));
    } catch (error) {
        console.error("Error in handleSendTextOnly:", error);
        LogFirebase("AskAI", "handleSendTextOnly_catch", error?.message);
        dispatch(setIsAIResponding(false));
        dispatch(resetPromptSelection());
    }
};

export const checkButtonDisabled = () => async (dispatch, getState) => {
    // Button is disabled if:
    // 1. No remote participant joined
    // 2. Current screen is notify screen
    // 3. Video stream is not active
    const { currentCallBodyScreen } = getState().commonReducer;
    const { livePointerInProgress } = getState().livePointer;
    const { extractTextInprogress } = getState().extractText;

    return (
        uploadFilesService.isUploadFilesView() ||
        currentCallBodyScreen === CALL_BODY_SCREEN.NOTIFY_SCREEN ||
        livePointerInProgress ||
        extractTextInprogress === EXTRACT_TEXT_VIEW.PREPARE_VIEW ||
        extractTextInprogress === EXTRACT_TEXT_VIEW.OCR_VIEW
    );
};

/**
 * Handle smart action button click
 * Auto-sends the selected action to Gemini and gets text response + new actions
 * @param {string} screenshot - Base64 screenshot
 * @param {Object} action - The smart action object that was clicked
 */
export const handleSmartActionClick = (screenshot, action) => async (dispatch, getState) => {
    try {
        LogFirebase("AskAI", "handleSmartActionClick_started", action.label);

        if (!screenshot || !action) {
            LogFirebase("AskAI", "handleSmartActionClick_missing_params", "");
            return;
        }

        // Highlight selected action button (will be cleared after 2 seconds)
        dispatch(setSelectedActionId(action.id));
        setTimeout(() => {
            dispatch(clearSelectedActionId());
        }, 2000);

        // Hide the "Snapshot 1" bubble (first Smart Actions bubble with header)
        dispatch(hideSnapshotHeaderBubble());

        // Get current conversation history for context (snapshot-specific)
        const { messages, currentSnapshotId } = getState().askAIReducer;
        const snapshotHistory = messages.filter((msg) => msg.snapshotId === currentSnapshotId);

        // Add user message showing the selected action (NO IMAGE - text only)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.USER,
                image: null, // Do NOT show image again
                text: action.label, // Show the action label (not the full prompt)
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
                selectedAction: action.label,
            })
        );

        // Set AI responding state
        dispatch(setIsAIResponding(true));
        dispatch(setIsGeneratingSmartActions(true));

        // Call Gemini with image + selected action prompt + conversation history
        // This returns BOTH text response AND new smart actions
        const result = await generateResponseWithActions(screenshot, action.prompt, snapshotHistory);

        // Add AI Response as a SEPARATE message bubble (FIRST)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.AI,
                text: result.textResponse,
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
            })
        );

        // Add Smart Actions as a SEPARATE message bubble (SECOND - NO "Snapshot 1" header)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.SMART_ACTIONS,
                actions: result.actions,
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
                showHeader: false, // Do NOT show "Snapshot 1" header for subsequent Smart Actions
            })
        );

        // Update smart actions in Redux (for backward compatibility with footer display)
        dispatch(setSmartActions(result.actions));

        // Show suggestion banner if Gemini recommends new snapshot
        if (result.suggestNewSnapshot) {
            dispatch(setSuggestNewSnapshot(true));
        }

        // DO NOT clear screenshot - keep it for chaining subsequent Smart Action clicks
        // Screenshot will only be cleared when:
        // 1. User clicks "Screenshot + Ask" again (new snapshot captured)
        // 2. User clicks "Quick Fix" again (new snapshot captured)
        // 3. User closes Ask AI drawer (full reset)

        // Reset states
        dispatch(setIsAIResponding(false));
        dispatch(setIsGeneratingSmartActions(false));

        LogFirebase("AskAI", "handleSmartActionClick_success", {
            textLength: result.textResponse.length,
            actionCount: result.actions.length,
            suggestNewSnapshot: result.suggestNewSnapshot,
        });
    } catch (error) {
        console.error("Error in handleSmartActionClick:", error);
        LogFirebase("AskAI", "handleSmartActionClick_error", error?.message);

        // On error, add error message as AI response bubble
        const { currentSnapshotId } = getState().askAIReducer;
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.AI,
                text: "I encountered an error processing your request. Please try again or try a different action.",
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
            })
        );

        // Add fallback Smart Actions as message bubble (without header)
        dispatch(
            addMessage({
                type: ASK_AI_MESSAGE_TYPES.SMART_ACTIONS,
                actions: [],
                timestamp: new Date().toISOString(),
                snapshotId: currentSnapshotId,
                showHeader: false,
            })
        );

        // Update Redux state (for backward compatibility)
        dispatch(setSmartActions([]));
        dispatch(setIsAIResponding(false));
        dispatch(setIsGeneratingSmartActions(false));
    }
};
